import type { ReactElement } from "react";
import { useTranslation } from "react-i18next";
import { getFirebase } from "../firebase/init.js";
import { applyWriteMap } from "../firebase/writes.js";
import { type EditableCharacter, toggleStressBox } from "../lib/character-edits.js";

interface Props {
  characterPath: string;
  character: EditableCharacter;
  /** Track ids → label keys. Defaults to physical + mental. */
  tracks?: Array<{ id: string; labelKey: string; boxCount: number }>;
}

const DEFAULT_TRACKS = [
  { id: "physical", labelKey: "tracks.physical", boxCount: 2 },
  { id: "mental", labelKey: "tracks.mental", boxCount: 2 },
];

export default function CharacterStressEditor({
  characterPath,
  character,
  tracks = DEFAULT_TRACKS,
}: Props): ReactElement {
  const { t } = useTranslation();

  async function onToggle(trackId: string, idx: number): Promise<void> {
    const { db } = getFirebase();
    await applyWriteMap(db, toggleStressBox(characterPath, character, trackId, idx));
  }

  return (
    <section>
      <h2 className="text-sm font-semibold uppercase text-muted-foreground">{t("sheet.stress")}</h2>
      <ul className="mt-1 space-y-1 text-sm">
        {tracks.map((tr) => {
          const boxes =
            character.stressState?.[tr.id] ?? new Array<boolean>(tr.boxCount).fill(false);
          return (
            <li key={tr.id} className="flex items-center gap-2">
              <span className="w-20">{t(tr.labelKey)}</span>
              <div className="flex gap-1">
                {boxes.map((checked, idx) => (
                  <button
                    type="button"
                    key={`${tr.id}-${idx}`}
                    aria-label={`${tr.id} box ${idx + 1}`}
                    aria-pressed={checked}
                    className={`h-6 w-6 rounded border text-xs ${
                      checked ? "bg-rose-500 text-white" : "bg-white"
                    }`}
                    onClick={() => onToggle(tr.id, idx)}
                  >
                    {checked ? "×" : ""}
                  </button>
                ))}
              </div>
            </li>
          );
        })}
      </ul>
    </section>
  );
}
